import BugReportIcon from "@mui/icons-material/BugReport";
import FeedbackIcon from "@mui/icons-material/Feedback";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CommentIcon from "@mui/icons-material/Comment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const LABEL_CONFIG = {
  bug: { label: "Bug", color: "error", icon: <BugReportIcon fontSize="small" /> },
  improvement: { label: "Melhoria", color: "warning", icon: <LightbulbIcon fontSize="small" /> },
  feature: { label: "Funcionalidade", color: "info", icon: <FeedbackIcon fontSize="small" /> },
};

const PRIORITY_CONFIG = {
  "priority:low":    { label: "Baixa",  color: "success" },
  "priority:medium": { label: "Média",  color: "warning" },
  "priority:high":   { label: "Alta",   color: "error"   },
};

const pulseStyle = `
@keyframes feedbackPulse {
  0%   { box-shadow: 0 0 0 0 rgba(211,47,47,0.5); }
  70%  { box-shadow: 0 0 0 12px rgba(211,47,47,0); }
  100% { box-shadow: 0 0 0 0 rgba(211,47,47,0); }
}
`;

export const FeedbackTrigger = ({ isAdmin = false, pendingCount = 0, onClick }) => (
  <>
    {isAdmin && pendingCount > 0 && <style>{pulseStyle}</style>}
    <Tooltip title="Reportar problema ou sugestão">
      <Badge
        badgeContent={isAdmin ? pendingCount : 0}
        color="error"
        overlap="circular"
        sx={{ "& .MuiBadge-badge": { pointerEvents: "none" } }}
      >
        <IconButton
          color="inherit"
          onClick={onClick}
          sx={{
            borderRadius: "50%",
            padding: 0.75,
            "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            ...(isAdmin && pendingCount > 0 && { animation: "feedbackPulse 1.5s infinite" }),
          }}
        >
          <FeedbackIcon />
        </IconButton>
      </Badge>
    </Tooltip>
  </>
);

const FeedbackModal = ({ open, onClose, pendingCount = 0, setPendingCount = () => {} }) => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";

  const [view, setView] = useState("list");
  const [issues, setIssues] = useState([]);
  const [loadingIssues, setLoadingIssues] = useState(false);
  const [search, setSearch] = useState("");
  const [filterLabel, setFilterLabel] = useState("");
  const [filterState, setFilterState] = useState("open");
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [comment, setComment] = useState("");
  const [loadingComment, setLoadingComment] = useState(false);
  const [issueComments, setIssueComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [loadingApprove, setLoadingApprove] = useState(false);
  const [loadingReject, setLoadingReject] = useState(false);
  const [loadingPriority, setLoadingPriority] = useState(false);
  const [type, setType] = useState("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [loadingNew, setLoadingNew] = useState(false);

  const fetchPendingCount = () => {
    if (!isAdmin) return;
    api.get("/feedback/pending-count")
      .then(({ data }) => setPendingCount(data.count))
      .catch(() => {});
  };

  useEffect(() => {
    fetchPendingCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    if (open) setView("list");
  }, [open]);

  const fetchIssues = async () => {
    setLoadingIssues(true);
    try {
      const params = { state: filterState };
      if (filterLabel) params.label = filterLabel;
      if (search) params.search = search;
      const { data } = await api.get("/feedback", { params });
      setIssues(data);
    } catch {
      toast.error("Erro ao carregar solicitações.");
    } finally {
      setLoadingIssues(false);
    }
  };

  useEffect(() => {
    if (open && view === "list") fetchIssues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, view, filterLabel, filterState]);

  const handleClose = () => {
    onClose();
    setSearch("");
    setFilterLabel("");
    setFilterState("open");
    setSelectedIssue(null);
    setComment("");
    setIssueComments([]);
    setType("bug");
    setTitle("");
    setDescription("");
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") fetchIssues();
  };

  const fetchComments = async (number) => {
    setLoadingComments(true);
    try {
      const { data } = await api.get(`/feedback/${number}/comments`);
      setIssueComments(data);
    } catch {
      setIssueComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleSelectIssue = (issue) => {
    setSelectedIssue(issue);
    setComment("");
    setIssueComments([]);
    setView("detail");
    fetchComments(issue.number);
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    setLoadingComment(true);
    try {
      await api.post(`/feedback/${selectedIssue.number}/comments`, { comment });
      toast.success("Comentário adicionado!");
      setComment("");
      setSelectedIssue(prev => ({ ...prev, comments: prev.comments + 1 }));
      fetchComments(selectedIssue.number);
    } catch {
      toast.error("Erro ao adicionar comentário.");
    } finally {
      setLoadingComment(false);
    }
  };

  const FEEDBACK_TOAST_ID = "feedback-action";

  const handleApprove = async () => {
    setLoadingApprove(true);
    try {
      await api.post(`/feedback/${selectedIssue.number}/approve`, { comment: comment.trim() || undefined });
      toast.success("Solicitação aprovada!", { toastId: FEEDBACK_TOAST_ID, updateId: FEEDBACK_TOAST_ID });
      setComment("");
      setSelectedIssue(prev => ({ ...prev, labels: [...prev.labels.filter(l => l !== "approved"), "approved"] }));
      setPendingCount(prev => Math.max(0, prev - 1));
      fetchComments(selectedIssue.number);
    } catch {
      toast.error("Erro ao aprovar solicitação.", { toastId: FEEDBACK_TOAST_ID, updateId: FEEDBACK_TOAST_ID });
    } finally {
      setLoadingApprove(false);
    }
  };

  const handleReject = async () => {
    if (!comment.trim()) {
      toast.error("Informe o motivo da recusa antes de prosseguir.", { toastId: FEEDBACK_TOAST_ID, updateId: FEEDBACK_TOAST_ID });
      return;
    }
    setLoadingReject(true);
    try {
      await api.post(`/feedback/${selectedIssue.number}/reject`, { comment: comment.trim() });
      toast.success("Solicitação recusada.", { toastId: FEEDBACK_TOAST_ID, updateId: FEEDBACK_TOAST_ID });
      setComment("");
      setView("list");
      setPendingCount(prev => Math.max(0, prev - 1));
      setTimeout(() => fetchIssues(), 1000);
    } catch {
      toast.error("Erro ao recusar solicitação.", { toastId: FEEDBACK_TOAST_ID, updateId: FEEDBACK_TOAST_ID });
    } finally {
      setLoadingReject(false);
    }
  };

  const handleSetPriority = async (priority) => {
    setLoadingPriority(true);
    try {
      const { data } = await api.patch(`/feedback/${selectedIssue.number}/priority`, { priority });
      setSelectedIssue(prev => ({ ...prev, labels: data.labels }));
      toast.success("Prioridade definida!", { toastId: FEEDBACK_TOAST_ID, updateId: FEEDBACK_TOAST_ID });
    } catch {
      toast.error("Erro ao definir prioridade.", { toastId: FEEDBACK_TOAST_ID, updateId: FEEDBACK_TOAST_ID });
    } finally {
      setLoadingPriority(false);
    }
  };

  const handleSubmitNew = async () => {
    if (!title.trim() || !description.trim()) {
      toast.error("Preencha o título e a descrição.");
      return;
    }
    setLoadingNew(true);
    try {
      await api.post("/feedback", {
        type, title: title.trim(), description: description.trim(),
        page: window.location.pathname, browser: navigator.userAgent,
      });
      toast.success("Solicitação enviada! Ela estará disponível na lista em instantes.");
      setView("list");
      setTitle(""); setDescription(""); setType("bug");
      fetchPendingCount();
      setTimeout(() => fetchIssues(), 3000);
    } catch {
      toast.error("Erro ao enviar solicitação.");
    } finally {
      setLoadingNew(false);
    }
  };

  const getIssueLabel = (labels) => {
    for (const key of Object.keys(LABEL_CONFIG)) {
      if (labels.includes(key)) return LABEL_CONFIG[key];
    }
    return null;
  };

  const getIssuePriority = (labels) => {
    for (const key of Object.keys(PRIORITY_CONFIG)) {
      if (labels.includes(key)) return PRIORITY_CONFIG[key];
    }
    return null;
  };

  const parseReportedBy = (body) => {
    const match = body?.match(/\*\*Reportado por:\*\* (.+?) \(/);
    return match ? match[1] : null;
  };

  if (!user?.id) return null;

  const isApproved = selectedIssue?.labels?.includes("approved");
  const currentPriority = selectedIssue ? Object.keys(PRIORITY_CONFIG).find(k => selectedIssue.labels.includes(k)) : null;

  const dialogTitle = view === "list"
    ? (filterState === "open" ? "Solicitações abertas" : "Solicitações encerradas")
    : view === "detail"
      ? `#${selectedIssue?.number} — ${selectedIssue?.title}`
      : "Nova solicitação";

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {(view === "detail" || view === "new") && (
            <IconButton size="small" onClick={() => setView("list")} sx={{ mr: 0.5 }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flex: 1, fontSize: "1rem" }}>
            {dialogTitle}
          </Typography>
          {view === "list" && (
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setView("new")}>
              Nova
            </Button>
          )}
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>

          {/* ── LISTA ── */}
          {view === "list" && (
            <Box sx={{ display: "flex", flexDirection: "column", height: 420 }}>
              <Box sx={{ p: 2, pb: 1, display: "flex", gap: 1, flexDirection: "column" }}>
                <TextField
                  size="small" fullWidth placeholder="Buscar por título... (Enter)"
                  value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={handleSearch}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                />
                <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                  <Chip label="Abertas" size="small"
                    variant={filterState === "open" ? "filled" : "outlined"}
                    color={filterState === "open" ? "success" : "default"}
                    onClick={() => setFilterState("open")} clickable
                  />
                  <Chip label="Encerradas" size="small"
                    variant={filterState === "closed" ? "filled" : "outlined"}
                    color="default"
                    onClick={() => setFilterState("closed")} clickable
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {["", "bug", "improvement", "feature"].map((lbl) => (
                    <Chip
                      key={lbl || "all"}
                      label={lbl ? LABEL_CONFIG[lbl]?.label : "Todos"}
                      size="small"
                      variant={filterLabel === lbl ? "filled" : "outlined"}
                      color={lbl && filterLabel === lbl ? LABEL_CONFIG[lbl]?.color : "default"}
                      onClick={() => setFilterLabel(lbl)} clickable
                    />
                  ))}
                </Box>
              </Box>
              <Divider />
              {loadingIssues ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : issues.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
                  <Typography variant="body2" color="textSecondary">Nenhuma solicitação encontrada.</Typography>
                </Box>
              ) : (
                <List dense sx={{ overflow: "auto", flex: 1 }}>
                  {issues.map((issue) => {
                    const cfg = getIssueLabel(issue.labels);
                    const prio = getIssuePriority(issue.labels);
                    const approved = issue.labels.includes("approved");
                    const reporter = parseReportedBy(issue.body);
                    return (
                      <React.Fragment key={issue.number}>
                        <ListItem button onClick={() => handleSelectIssue(issue)} sx={{ py: 1.5, px: 2, alignItems: "flex-start", backgroundColor: (!approved && issue.state === "open" && isAdmin) ? "rgba(255,152,0,0.08)" : "inherit", "&:hover": { backgroundColor: (!approved && issue.state === "open" && isAdmin) ? "rgba(255,152,0,0.14)" : undefined } }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                {cfg && <Chip label={cfg.label} size="small" color={cfg.color} icon={cfg.icon} />}
                                {prio && <Chip label={prio.label} size="small" color={prio.color} variant="outlined" />}
                                {approved && <Chip label="Aprovada" size="small" color="success" variant="outlined" />}
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  #{issue.number} {issue.title}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Typography variant="caption" color="textSecondary">
                                {reporter ? `por ${reporter} · ` : ""}
                                {issue.comments > 0 ? `${issue.comments} comentário(s) · ` : ""}
                                {new Date(issue.createdAt).toLocaleDateString("pt-BR")}
                              </Typography>
                            }
                          />
                        </ListItem>
                        <Divider component="li" />
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Box>
          )}

          {/* ── DETALHE ── */}
          {view === "detail" && selectedIssue && (
            <Box sx={{ display: "flex", flexDirection: "column", height: 420, overflow: "hidden" }}>

              {/* ── Cabeçalho fixo: chips + prioridade + ações admin ── */}
              <Box sx={{ p: 2, pb: 1.5, borderBottom: 1, borderColor: "divider" }}>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                  {getIssueLabel(selectedIssue.labels) && (
                    <Chip label={getIssueLabel(selectedIssue.labels).label} size="small" color={getIssueLabel(selectedIssue.labels).color} />
                  )}
                  {isApproved && <Chip label="Aprovada" size="small" color="success" icon={<CheckCircleIcon fontSize="small" />} />}
                  {getIssuePriority(selectedIssue.labels) && (
                    <Chip label={getIssuePriority(selectedIssue.labels).label} size="small" color={getIssuePriority(selectedIssue.labels).color} variant="outlined" />
                  )}
                </Box>

                {isAdmin && selectedIssue.state === "open" && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="caption" color="textSecondary">Prioridade:</Typography>
                    {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                      <Chip key={key} label={cfg.label} size="small" color={cfg.color}
                        variant={currentPriority === key ? "filled" : "outlined"}
                        onClick={() => !loadingPriority && handleSetPriority(key)}
                        clickable disabled={loadingPriority}
                      />
                    ))}
                    <Box sx={{ ml: "auto" }}>
                      <Button variant="contained" size="small" color="success"
                        startIcon={loadingApprove ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
                        disabled={loadingApprove || loadingReject || isApproved}
                        onClick={handleApprove}
                      >
                        {isApproved ? "Aprovada" : "Aprovar"}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* ── Corpo rolável ── */}
              <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {selectedIssue.body?.replace(/\*\*/g, "").replace(/---/, "").trim()}
                </Typography>

                {/* ── Comentários existentes ── */}
                {(loadingComments || issueComments.length > 0) && (
                  <Box sx={{ mt: 2, borderTop: 1, borderColor: "divider", pt: 2 }}>
                    <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: "block" }}>
                      {loadingComments ? "Carregando comentários..." : `${issueComments.length} comentário(s)`}
                    </Typography>
                    {issueComments.map(c => (
                      <Box key={c.id} sx={{ mb: 1.5, p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}>
                        <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 0.5 }}>
                          {new Date(c.createdAt).toLocaleString("pt-BR")}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {c.body}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {/* ── Comentário fixo no rodapé ── */}
              <Box sx={{ p: 2, pt: 1.5, borderTop: 1, borderColor: "divider" }}>
                <Typography variant="subtitle2" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CommentIcon fontSize="small" />
                  {isAdmin && selectedIssue.state === "open" ? "Comentário / Motivo da recusa" : "Adicionar comentário"}
                </Typography>
                <TextField
                  multiline rows={2} fullWidth size="small"
                  placeholder={
                    isAdmin && selectedIssue.state === "open"
                      ? "Opcional para aprovar · Obrigatório para recusar..."
                      : "Descreva informações adicionais..."
                  }
                  value={comment} onChange={(e) => setComment(e.target.value)}
                />
                <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                  <Button variant="outlined" size="small"
                    disabled={!comment.trim() || loadingComment} onClick={handleAddComment}
                    startIcon={loadingComment ? <CircularProgress size={14} color="inherit" /> : null}
                  >
                    Comentar
                  </Button>
                  {isAdmin && selectedIssue.state === "open" && !isApproved && (
                    <Button variant="outlined" size="small" color="error"
                      startIcon={loadingReject ? <CircularProgress size={14} color="inherit" /> : <CancelIcon />}
                      disabled={loadingApprove || loadingReject}
                      onClick={handleReject}
                    >
                      Recusar
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* ── NOVA SOLICITAÇÃO ── */}
          {view === "new" && (
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
              <ToggleButtonGroup value={type} exclusive onChange={(_, val) => val && setType(val)} size="small" fullWidth>
                <ToggleButton value="bug"><BugReportIcon sx={{ mr: 1 }} fontSize="small" /> Bug</ToggleButton>
                <ToggleButton value="improvement"><LightbulbIcon sx={{ mr: 1 }} fontSize="small" /> Melhoria</ToggleButton>
                <ToggleButton value="feature"><FeedbackIcon sx={{ mr: 1 }} fontSize="small" /> Funcionalidade</ToggleButton>
              </ToggleButtonGroup>
              <TextField label="Título" variant="outlined" fullWidth size="small"
                value={title} onChange={(e) => setTitle(e.target.value)} inputProps={{ maxLength: 100 }} />
              <TextField label="Descrição" variant="outlined" fullWidth multiline rows={4} size="small"
                value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder="Descreva com detalhes o problema ou a sugestão..." />
              <Typography variant="caption" color="textSecondary">
                Serão enviados automaticamente: seu nome, perfil, página atual e navegador.
              </Typography>
            </Box>
          )}
        </DialogContent>

        {view === "new" && (
          <DialogActions>
            <Button onClick={() => setView("list")} color="secondary" variant="outlined" disabled={loadingNew}>Cancelar</Button>
            <Button onClick={handleSubmitNew} color="primary" variant="contained"
              disabled={loadingNew || !title.trim() || !description.trim()}
              startIcon={loadingNew ? <CircularProgress size={16} color="inherit" /> : null}>
              Enviar
            </Button>
          </DialogActions>
        )}
        {view !== "new" && (
          <DialogActions>
            <Button onClick={handleClose} color="secondary" variant="outlined">Fechar</Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default FeedbackModal;
